/**
 * DokumenteSection.tsx — Überarbeitete Dokumenten-Ansicht für Akten
 * 
 * Features:
 * - Sticky Header mit Akten-Info
 * - Filter-Leiste (nach Kategorie, Typ)
 * - Checkboxen für Dokumenten-Auswahl
 * - "Versenden" Button → öffnet DokumenteVersandPanel
 * - Sauberes, nicht zu buntes Design
 */
import { useState, useEffect, useMemo } from 'react';
import { FileText, Download, Filter, Send, CheckSquare, Square, Loader2, Search, Package, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import DokumenteVersandPanel from './DokumenteVersandPanel';

interface Dokument {
  id: string;
  dateiname: string;
  dateipfad: string;
  mime_type: string;
  dateigroesse: number;
  titel?: string;
  kategorie?: string;
  dokument_typ?: string;
  erstellt_am?: string;
  tags?: string[];
}

interface DokumenteSectionProps {
  akteId: string;
  aktenNr: string;
  betriebId?: string;
  reloadKey?: number;
}

const KATEGORIE_LABELS: Record<string, string> = {
  gutachten: 'Gutachten',
  gutachten_bundle: 'Gutachten',
  kalkulation: 'Kalkulation',
  kalkulation_protokoll: 'Kalkulation',
  bewertung: 'Bewertung',
  rechnung: 'Rechnung',
  auftrag: 'Auftrag',
  stellungnahme: 'Stellungnahme',
  anschreiben: 'Anschreiben',
  mietvertrag: 'Mietvertrag',
  abtretung: 'Abtretung',
  sonstige: 'Sonstige',
};

const KATEGORIE_ORDER = ['gutachten', 'gutachten_bundle', 'kalkulation', 'kalkulation_protokoll', 'bewertung', 'rechnung', 'auftrag', 'stellungnahme', 'anschreiben', 'mietvertrag', 'abtretung', 'sonstige'];

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function DokumenteSection({ akteId, aktenNr, betriebId, reloadKey }: DokumenteSectionProps) {
  const [docs, setDocs] = useState<Dokument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterKategorie, setFilterKategorie] = useState<string>('alle');
  const [searchText, setSearchText] = useState('');
  const [showVersandPanel, setShowVersandPanel] = useState(false);

  // Load documents
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('dokumente')
        .select('id,dateiname,dateipfad,mime_type,dateigroesse,kategorie,dokument_typ,titel,erstellt_am,tags')
        .eq('akte_id', akteId)
        .not('kategorie', 'in', '(lichtbilder,fotos)')
        .order('erstellt_am', { ascending: false });
      if (!cancelled) {
        setDocs(data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [akteId, reloadKey]);

  // Available categories for filter
  const categories = useMemo(() => {
    const cats = new Set(docs.map(d => d.kategorie || 'sonstige'));
    return KATEGORIE_ORDER.filter(c => cats.has(c));
  }, [docs]);

  // Filtered documents
  const filteredDocs = useMemo(() => {
    let result = docs;
    if (filterKategorie !== 'alle') {
      result = result.filter(d => (d.kategorie || 'sonstige') === filterKategorie);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(d => 
        (d.dateiname || '').toLowerCase().includes(q) ||
        (d.titel || '').toLowerCase().includes(q) ||
        (d.kategorie || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [docs, filterKategorie, searchText]);

  // Grouped by category
  const grouped = useMemo(() => {
    const groups: Record<string, Dokument[]> = {};
    filteredDocs.forEach(d => {
      const k = d.kategorie || 'sonstige';
      (groups[k] = groups[k] || []).push(d);
    });
    return groups;
  }, [filteredDocs]);

  const groupKeys = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      const ia = KATEGORIE_ORDER.indexOf(a);
      const ib = KATEGORIE_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [grouped]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredDocs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocs.map(d => d.id)));
    }
  };

  const selectedDocs = docs.filter(d => selectedIds.has(d.id));

  const openDoc = async (doc: Dokument) => {
    const { data, error } = await supabase.storage.from('dokumente').createSignedUrl(doc.dateipfad, 3600);
    if (error || !data?.signedUrl) { toast.error('Konnte Dokument nicht öffnen'); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  };

  return (
    <div className="space-y-0">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 -mx-4 px-4 sm:-mx-5 sm:px-5 pb-3 pt-1">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <FileText className="w-4 h-4 text-gray-600" />
            <h3 className="text-[14px] font-semibold text-gray-900">Dokumente</h3>
            <span className="text-[12px] text-gray-400">{docs.length} Dateien</span>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button
                onClick={() => setShowVersandPanel(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-[12px] font-medium hover:bg-gray-800 transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                {selectedIds.size} versenden
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sticky top-[52px] z-10 bg-white border-b border-gray-50 -mx-4 px-4 sm:-mx-5 sm:px-5 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[140px] max-w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Suchen..."
              className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 placeholder:text-gray-400"
            />
          </div>

          {/* Category filter chips */}
          <button
            onClick={() => setFilterKategorie('alle')}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
              filterKategorie === 'alle' 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Alle
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterKategorie(cat === filterKategorie ? 'alle' : cat)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                filterKategorie === cat 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {KATEGORIE_LABELS[cat] || cat}
            </button>
          ))}

          {/* Select all toggle */}
          <div className="ml-auto">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
            >
              {selectedIds.size === filteredDocs.length && filteredDocs.length > 0 ? (
                <CheckSquare className="w-3.5 h-3.5" />
              ) : (
                <Square className="w-3.5 h-3.5" />
              )}
              Alle
            </button>
          </div>
        </div>
      </div>

      {/* Document List */}
      <div className="pt-3 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-[13px] text-gray-400">
              {docs.length === 0 ? 'Noch keine Dokumente vorhanden.' : 'Keine Dokumente für diesen Filter.'}
            </p>
          </div>
        ) : (
          groupKeys.map(k => (
            <div key={k}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                {KATEGORIE_LABELS[k] || k} <span className="font-normal">· {grouped[k].length}</span>
              </p>
              <div className="space-y-1.5">
                {grouped[k].map(doc => (
                  <div
                    key={doc.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors cursor-pointer group ${
                      selectedIds.has(doc.id)
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelect(doc.id)}
                      className="flex-shrink-0 w-5 h-5 flex items-center justify-center"
                    >
                      {selectedIds.has(doc.id) ? (
                        <CheckSquare className="w-4 h-4 text-gray-900" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                      )}
                    </button>

                    {/* Icon */}
                    <div className="w-7 h-7 rounded-md bg-gray-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-3.5 h-3.5 text-gray-500" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" onClick={() => openDoc(doc)}>
                      <p className="text-[13px] font-medium text-gray-900 truncate">{doc.titel || doc.dateiname}</p>
                      <p className="text-[11px] text-gray-400">
                        {doc.dokument_typ || doc.kategorie || 'Dokument'} · {formatBytes(doc.dateigroesse)}
                        {doc.erstellt_am && ` · ${new Date(doc.erstellt_am).toLocaleDateString('de-DE')}`}
                      </p>
                    </div>

                    {/* Actions */}
                    <button
                      onClick={() => openDoc(doc)}
                      className="flex-shrink-0 px-2.5 py-1 rounded-md text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Selection Footer */}
      {selectedIds.size > 0 && (
        <div className="sticky bottom-0 -mx-4 px-4 sm:-mx-5 sm:px-5 py-3 bg-white border-t border-gray-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium text-gray-700">{selectedIds.size} ausgewählt</span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] text-gray-400 hover:text-gray-600 underline"
            >
              Auswahl aufheben
            </button>
          </div>
          <button
            onClick={() => setShowVersandPanel(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-[12px] font-medium hover:bg-gray-800 transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            Versenden
          </button>
        </div>
      )}

      {/* Versand Panel */}
      <DokumenteVersandPanel
        open={showVersandPanel}
        onClose={() => setShowVersandPanel(false)}
        dokumente={selectedDocs}
        akteId={akteId}
        aktenNr={aktenNr}
        betriebId={betriebId}
      />
    </div>
  );
}
